import { useState, useEffect } from 'react';
import {
  Wrench, Sparkles, Star, Zap, ArrowRight, Plus, Lock,
  CheckCircle, AlertCircle, Package, Trophy, Crown, Flame
} from 'lucide-react';
import useUserStore from '../store/userStore';

// Component tier system
const TIERS = {
  basic: { name: 'Basic', color: 'gray', stars: 1 },
  improved: { name: 'Improved', color: 'green', stars: 2 },
  advanced: { name: 'Advanced', color: 'blue', stars: 3 },
  elite: { name: 'Elite', color: 'purple', stars: 4 },
  legendary: { name: 'Legendary', color: 'yellow', stars: 5 },
};

// Crafting recipes
const CRAFTING_RECIPES = [
  {
    id: 'redis_cluster',
    name: 'Redis Cluster',
    description: 'High-availability distributed cache with automatic failover',
    result: { id: 101, name: 'Redis Cluster', category: 'cache', tier: 'elite' },
    requirements: [
      { componentId: 6, name: 'Redis Cache', count: 2 },
      { componentId: 11, name: 'Nginx Load Balancer', count: 1 },
    ],
    dpCost: 200,
    bonusStats: { qps: '+200%', latency: '-50%' },
  },
  {
    id: 'postgres_ha',
    name: 'PostgreSQL HA Cluster',
    description: 'Master-slave replication with automatic failover',
    result: { id: 102, name: 'PostgreSQL HA', category: 'database', tier: 'elite' },
    requirements: [
      { componentId: 7, name: 'PostgreSQL', count: 3 },
    ],
    dpCost: 300,
    bonusStats: { reliability: '+99.99%', qps: '+100%' },
  },
  {
    id: 'event_bus',
    name: 'Event Bus',
    description: 'Distributed event-driven messaging system',
    result: { id: 103, name: 'Event Bus', category: 'queue', tier: 'advanced' },
    requirements: [
      { componentId: 14, name: 'Kafka Message Queue', count: 1 },
      { componentId: 6, name: 'Redis Cache', count: 1 },
    ],
    dpCost: 250,
    bonusStats: { throughput: '+500%', decoupling: 'Full' },
  },
  {
    id: 'cdn_global',
    name: 'Global CDN',
    description: 'Worldwide content delivery network',
    result: { id: 104, name: 'Global CDN', category: 'network', tier: 'legendary' },
    requirements: [
      { componentId: 11, name: 'Nginx Load Balancer', count: 2 },
      { componentId: 6, name: 'Redis Cache', count: 2 },
    ],
    dpCost: 500,
    bonusStats: { latency: '-80%', availability: '99.99%' },
  },
];

// Component upgrade paths
const UPGRADE_PATHS = [
  {
    baseId: 1,
    baseName: 'Basic Web Server',
    upgrades: [
      { tier: 'improved', dpCost: 50, name: 'Enhanced Web Server', bonus: '+25% QPS' },
      { tier: 'advanced', dpCost: 100, name: 'Scalable Web Server', bonus: '+50% QPS, Auto-scaling' },
      { tier: 'elite', dpCost: 200, name: 'Enterprise Web Server', bonus: '+100% QPS, HA' },
    ],
  },
  {
    baseId: 2,
    baseName: 'Simple Database',
    upgrades: [
      { tier: 'improved', dpCost: 75, name: 'Indexed Database', bonus: '+30% Query Speed' },
      { tier: 'advanced', dpCost: 150, name: 'Replicated Database', bonus: 'Read Replicas' },
      { tier: 'elite', dpCost: 300, name: 'Sharded Database', bonus: 'Horizontal Scaling' },
    ],
  },
  {
    baseId: 3,
    baseName: 'In-Memory Cache',
    upgrades: [
      { tier: 'improved', dpCost: 40, name: 'LRU Cache', bonus: 'Smart Eviction' },
      { tier: 'advanced', dpCost: 80, name: 'Distributed Cache', bonus: 'Multi-node' },
      { tier: 'elite', dpCost: 160, name: 'Persistent Cache', bonus: 'Durability' },
    ],
  },
];

const TierStars = ({ tier }) => {
  const tierInfo = TIERS[tier] || TIERS.basic;
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${
            i < tierInfo.stars
              ? `text-${tierInfo.color}-400 fill-${tierInfo.color}-400`
              : 'text-gray-300'
          }`}
          style={{
            color: i < tierInfo.stars ? (tierInfo.color === 'yellow' ? '#facc15' : tierInfo.color === 'purple' ? '#a855f7' : tierInfo.color === 'blue' ? '#3b82f6' : tierInfo.color === 'green' ? '#22c55e' : '#9ca3af') : '#d1d5db',
            fill: i < tierInfo.stars ? (tierInfo.color === 'yellow' ? '#facc15' : tierInfo.color === 'purple' ? '#a855f7' : tierInfo.color === 'blue' ? '#3b82f6' : tierInfo.color === 'green' ? '#22c55e' : '#9ca3af') : 'none'
          }}
        />
      ))}
    </div>
  );
};

const RecipeCard = ({ recipe, userInventory, userDP, onCraft }) => {
  const hasAllComponents = recipe.requirements.every(req => {
    const owned = userInventory.filter(c => c.id === req.componentId).length;
    return owned >= req.count;
  });
  const hasEnoughDP = userDP >= recipe.dpCost;
  const canCraft = hasAllComponents && hasEnoughDP;

  const tierInfo = TIERS[recipe.result.tier] || TIERS.basic;

  return (
    <div className={`
      relative bg-white rounded-xl border-2 overflow-hidden transition-all duration-300
      ${canCraft ? 'border-primary-300 hover:shadow-lg hover:border-primary-400' : 'border-gray-200 opacity-75'}
    `}>
      {/* Tier badge */}
      <div
        className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold text-white"
        style={{
          background: tierInfo.color === 'yellow' ? 'linear-gradient(135deg, #facc15, #f59e0b)' :
                      tierInfo.color === 'purple' ? 'linear-gradient(135deg, #a855f7, #7c3aed)' :
                      tierInfo.color === 'blue' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' :
                      tierInfo.color === 'green' ? 'linear-gradient(135deg, #22c55e, #16a34a)' :
                      'linear-gradient(135deg, #9ca3af, #6b7280)'
        }}
      >
        {tierInfo.name}
      </div>

      <div className="p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center">
            <Wrench className="w-7 h-7 text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 mb-1">{recipe.name}</h3>
            <p className="text-sm text-gray-500">{recipe.description}</p>
            <TierStars tier={recipe.result.tier} />
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="text-xs font-medium text-gray-500 mb-2">Required Components</div>
          <div className="space-y-2">
            {recipe.requirements.map((req, i) => {
              const owned = userInventory.filter(c => c.id === req.componentId).length;
              const hasSufficient = owned >= req.count;
              return (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className={hasSufficient ? 'text-gray-700' : 'text-red-600'}>
                    {req.name} x{req.count}
                  </span>
                  <span className={`font-medium ${hasSufficient ? 'text-green-600' : 'text-red-600'}`}>
                    {owned}/{req.count}
                    {hasSufficient ? <CheckCircle className="w-4 h-4 inline ml-1" /> : <AlertCircle className="w-4 h-4 inline ml-1" />}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bonus Stats */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(recipe.bonusStats).map(([stat, value]) => (
            <span key={stat} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              {stat}: {value}
            </span>
          ))}
        </div>

        {/* Craft button */}
        <button
          onClick={() => canCraft && onCraft(recipe)}
          disabled={!canCraft}
          className={`
            w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all
            ${canCraft
              ? 'bg-gradient-to-r from-primary-500 to-purple-500 text-white hover:from-primary-600 hover:to-purple-600'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
          `}
        >
          <Sparkles className="w-5 h-5" />
          Craft for {recipe.dpCost} DP
        </button>
      </div>
    </div>
  );
};

const UpgradeCard = ({ upgrade, currentTier, userDP, onUpgrade }) => {
  const tierInfo = TIERS[upgrade.tier];
  const canUpgrade = userDP >= upgrade.dpCost && currentTier < TIERS[upgrade.tier]?.stars;

  return (
    <div className={`
      flex items-center gap-4 p-4 rounded-lg border-2 transition-all
      ${canUpgrade ? 'border-blue-200 bg-blue-50 hover:border-blue-400' : 'border-gray-200 bg-gray-50 opacity-60'}
    `}>
      <div className="flex items-center gap-2">
        <TierStars tier={upgrade.tier} />
      </div>
      <div className="flex-1">
        <div className="font-medium text-gray-900">{upgrade.name}</div>
        <div className="text-sm text-gray-500">{upgrade.bonus}</div>
      </div>
      <button
        onClick={() => canUpgrade && onUpgrade(upgrade)}
        disabled={!canUpgrade}
        className={`
          px-4 py-2 rounded-lg font-medium flex items-center gap-1 transition-all
          ${canUpgrade
            ? 'bg-blue-500 text-white hover:bg-blue-600'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
        `}
      >
        <Zap className="w-4 h-4" />
        {upgrade.dpCost} DP
      </button>
    </div>
  );
};

const ComponentCrafting = () => {
  const { user, inventory } = useUserStore();
  const [activeTab, setActiveTab] = useState('craft');
  const [showCraftAnimation, setShowCraftAnimation] = useState(false);
  const [craftedItem, setCraftedItem] = useState(null);

  const handleCraft = (recipe) => {
    setCraftedItem(recipe.result);
    setShowCraftAnimation(true);
    setTimeout(() => setShowCraftAnimation(false), 3000);
  };

  const handleUpgrade = (upgrade) => {
    // In real app, this would call API and update inventory
    console.log('Upgrading to:', upgrade);
  };

  return (
    <div className="space-y-6">
      {/* Craft Animation Overlay */}
      {showCraftAnimation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-md text-center animate-bounce-in">
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-ping opacity-30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-12 h-12 text-yellow-500 animate-spin-slow" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Component Crafted!</h3>
            <p className="text-gray-600 mb-4">You created a new {craftedItem?.name}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full">
              <Trophy className="w-5 h-5 text-purple-500" />
              <span className="font-medium text-purple-700">Added to Inventory</span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Wrench className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Component Workshop</h2>
            <p className="text-purple-200">Craft & upgrade your architectural components</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('craft')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'craft'
                ? 'bg-white text-purple-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            Crafting
          </button>
          <button
            onClick={() => setActiveTab('upgrade')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'upgrade'
                ? 'bg-white text-purple-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Upgrades
          </button>
        </div>
      </div>

      {/* DP Balance */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-6 h-6 text-orange-500" />
          <span className="font-medium text-gray-700">Your Balance</span>
        </div>
        <div className="text-2xl font-bold text-orange-600">{user?.design_points || 0} DP</div>
      </div>

      {/* Crafting Tab */}
      {activeTab === 'craft' && (
        <div>
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-500" />
            Crafting Recipes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CRAFTING_RECIPES.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                userInventory={inventory || []}
                userDP={user?.design_points || 0}
                onCraft={handleCraft}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upgrades Tab */}
      {activeTab === 'upgrade' && (
        <div>
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Upgrade Paths
          </h3>
          <div className="space-y-6">
            {UPGRADE_PATHS.map((path) => (
              <div key={path.baseId} className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{path.baseName}</h4>
                    <TierStars tier="basic" />
                  </div>
                </div>
                <div className="space-y-3">
                  {path.upgrades.map((upgrade, i) => (
                    <UpgradeCard
                      key={i}
                      upgrade={upgrade}
                      currentTier={1}
                      userDP={user?.design_points || 0}
                      onUpgrade={handleUpgrade}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tier Legend */}
      <div className="bg-white rounded-xl border p-5">
        <h4 className="font-bold text-gray-900 mb-4">Component Tiers</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(TIERS).map(([key, tier]) => (
            <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
              <TierStars tier={key} />
              <span className="text-sm font-medium text-gray-700">{tier.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComponentCrafting;
